import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';

import { confirm } from 'devextreme/ui/dialog';

import _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { GraphqlService } from '../../graphql/graphql.service';
import { HelperService } from '../../shared/helper.service';
import { Agent, Breadcrumb, TagCategory, User } from '../../shared/models';
import { AbstractPage } from '../../shared/pages/page.abstract';

@Component({
	selector: 'pk-broker-agent-info',
	templateUrl: './agent-info.component.html',
	styleUrls: ['./agent-info.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class AgentInfoComponent extends AbstractPage {
	public static breadcrumbs = [
		{
			text: 'Dashboard',
			route: '/contract',
		},
		{
			text: 'Agent Information',
			isLast: true,
		},
	] as Breadcrumb[];

	public agent: Agent;
	public agentTree: Agent[] = [];
	public tagCategories: TagCategory[] = [];

	public window = window;

	constructor(
		private route: ActivatedRoute,
		private graphqlService: GraphqlService,
		private toastrService: ToastrService,
	) {
		super(AgentInfoComponent.breadcrumbs, 'Agent Information');
		this.route.paramMap.subscribe(async (paramMap: ParamMap) => {
			const agentId = paramMap.get('agentId');

			try {
				const result = await this.graphqlService.getAgentInfoData(agentId);
				this.agent = new Agent(result.data.agent);
				this.tagCategories = result.data.tagCategories.message;
				this.groupTagsByCategory(this.agent);
				this.agentTree.push(this.agent);
				await this.getAgentParent(this.agent);
			} catch (e) {
				this.warnings = HelperService.getErrorMessage(e);
			}

			this.loading = false;
		});
	}

	public async getAgentParent(agent: Agent): Promise<void> {
		if (agent.parentId) {
			try {
				const result = await this.graphqlService.getParentAgentInfoData(agent.parentId);
				agent.parent = new Agent(result.data.agent);
				this.groupTagsByCategory(agent.parent);
				this.agentTree.push(agent.parent);
				await this.getAgentParent(agent.parent);
			} catch (e) {
				this.warnings = HelperService.getErrorMessage(e);
			}
		}
	}

	get isAdmin(): boolean {
		return this.loggedInUser.isAdmin;
	}
	get isAdminOrOwner(): boolean {
		return this.isAdmin || this.loggedInUser.isOwner;
	}
	get stripeCustomerDashboard(): string {
		let baseUrl = 'https://dashboard.stripe.com/';
		if (this.environment.name !== 'production') {
			baseUrl += 'test/';
		}

		return baseUrl + 'customers/';
	}

	public impersonateUser(login: User): void {
		this.securityService.impersonateUser(login);
	}

	public async sendWelcomeEmail(login: User): Promise<void> {
		const result = await confirm('Are you sure you want to send this user the welcome email?  The password will be reset.', 'Confirm');
		if (result) {
			try {
				login['sendingWelcomeEmail'] = true;
				await this.graphqlService.sendWelcomeEmail(login.userId);
				this.toastrService.success('Welcome email sent successfully', 'Agent Information');
			} catch (e) {
				this.toastrService.warning(
					'There was a problem sending the welcome email. We have been notified and are working to fix the issue. Please check back again in 30 minutes.',
					'Agent Information',
				);
			}
			login['sendingWelcomeEmail'] = false;
		}
	}

	public groupTagsByCategory(agent: Agent): void {
		agent.tagsByCategory = _.groupBy(agent.tags, 'tagCategoryId');
	}
}
